// utils/exportAssetsToExcel.ts
import * as XLSX from "xlsx";
import { Asset, AssetPlanList, AssetConnectStatus } from "../models/asset";
import { IAssetSummary } from "../api/fetchMaintenance";

// Tipos das funções externas
type FetchMaintenanceFn = (
  summary: IAssetSummary[]
) => Promise<[Asset[], any?]>;
type FetchComplementaryFn = (ids: string[]) => Promise<any[]>;
type FetchCheckupsByChassisListFn = (
  chassisList: string[],
  assets: Asset[]
) => Promise<any[]>;
type FetchAssetsByRioPlansFn = (
  assets: IAssetSummary[]
) => Promise<{ [planName: string]: string[] }>;
type GetAssetRioPlanFn = (assetId: string) => { plans?: any } | undefined;

export async function exportAssetsToExcel({
  allSummaryAssets,
  assets,
  assetsPlans,
  assetsConnectionStatus,
  fetchMaintenance,
  fetchComplementaryAssetsDetails,
  fetchCheckupsByChassisList,
  fetchAssetsByRioPlans, // <<< NOVO
  getAssetRioPlan, // opcional (fallback)
  filename = `dados_manutencao_${new Date().toISOString().slice(0, 10)}.xlsx`,
  pageSize = 50,
  onChunk,
}: {
  allSummaryAssets?: IAssetSummary[];
  assets: Asset[];
  assetsPlans: AssetPlanList[];
  assetsConnectionStatus: AssetConnectStatus[];
  fetchMaintenance: FetchMaintenanceFn;
  fetchComplementaryAssetsDetails: FetchComplementaryFn;
  fetchCheckupsByChassisList: FetchCheckupsByChassisListFn;
  fetchAssetsByRioPlans: FetchAssetsByRioPlansFn; // <<< NOVO
  getAssetRioPlan?: GetAssetRioPlanFn; // opcional
  filename?: string;
  pageSize?: number;
  onChunk?: (
    stage: "maintenance" | "complementary" | "checkups" | "rioplans",
    index: number,
    total: number
  ) => void;
}): Promise<void> {
  // ==== helpers
  const chunk = <T>(arr: T[], size: number) =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size)
    );

  const indexBy = <T, K extends string | number>(
    list: T[] | undefined,
    key: (x: T) => K | undefined | null
  ): Record<K, T> =>
    (list ?? []).reduce((acc, item) => {
      const k = key(item);
      if (k !== undefined && k !== null) (acc as any)[k] = item;
      return acc;
    }, {} as Record<K, T>);

  const formatDate = (d?: Date | string | null) =>
    d ? new Date(d as any).toLocaleDateString("pt-BR") : "-";

  const normalizePlans = (value: any): string => {
    if (!value) return "";
    if (typeof value === "string") return value.trim();
    if (Array.isArray(value)) {
      return value
        .map((v) =>
          typeof v === "string"
            ? v.trim()
            : (v?.name ?? v?.description ?? v?.label ?? v?.plan ?? "")
                .toString()
                .trim()
        )
        .filter(Boolean)
        .join(", ");
    }
    return (
      value?.name ??
      value?.description ??
      value?.label ??
      value?.plan ??
      ""
    )
      .toString()
      .trim();
  };

  const pickPlate = (asset: Asset, comp: any, base?: Asset) => {
    const candidates = [
      asset.licensePlate,
      (asset as any)?.vehicle?.licensePlate,
      base?.licensePlate,
      (base as any)?.vehicle?.licensePlate,
      comp?.licensePlate,
      comp?.vehicle?.licensePlate,
      comp?.plate,
      comp?.plates?.[0]?.licensePlate,
      comp?.plates?.[0]?.plate,
    ].filter(Boolean) as string[];
    const normalize = (p: string) => p.trim().toUpperCase().replace(/\s+/g, "");
    return candidates.length ? normalize(candidates[0]!) : "-";
  };

  const normalizeConnection = (
    raw: unknown
  ): "Conectado" | "Não conectado" | undefined => {
    if (raw == null) return undefined;
    const s = String(raw).toLowerCase();
    if (
      ["online", "conectado", "connected", "available", "true", "1"].some((k) =>
        s.includes(k)
      )
    ) {
      return "Conectado";
    }
    if (
      [
        "offline",
        "desconectado",
        "disconnected",
        "unavailable",
        "false",
        "0",
      ].some((k) => s.includes(k))
    ) {
      return "Não conectado";
    }
    return undefined;
  };

  // ==== 0) summary base
  const summary: IAssetSummary[] =
    (allSummaryAssets?.length ? allSummaryAssets : []) ||
    assets.map((a) => ({
      id: a.id,
      identification: a.id ?? a.licensePlate ?? a.chassis ?? a.name ?? "",
      name: a.name ?? a.model ?? "",
    }));
  if (!summary.length) throw new Error("Sem dados para exportar.");

  // ==== 1) maintenance (paginado)
  const maintenanceChunks = chunk(summary, pageSize);
  const aggregated: Asset[] = [];
  for (let i = 0; i < maintenanceChunks.length; i++) {
    const c = maintenanceChunks[i];
    try {
      const [assetsPage] = await fetchMaintenance(c);
      if (Array.isArray(assetsPage) && assetsPage.length)
        aggregated.push(...assetsPage);
    } catch (e) {
      console.warn("Falha em bloco de manutenção (segue com fallback):", e);
    } finally {
      onChunk?.("maintenance", i + 1, maintenanceChunks.length);
    }
  }
  const sourceAssets: Asset[] = aggregated.length ? aggregated : assets || [];
  if (!sourceAssets.length)
    throw new Error("Não foi possível obter dados para exportar.");

  // ==== 2) complementares por assetId (paginado)
  const assetIds = sourceAssets.map((a) => a.id).filter(Boolean);
  const compChunks = chunk(assetIds, pageSize);
  let allComplementary: any[] = [];
  for (let i = 0; i < compChunks.length; i++) {
    const c = compChunks[i];
    try {
      const part = await fetchComplementaryAssetsDetails(c);
      if (Array.isArray(part) && part.length)
        allComplementary = allComplementary.concat(part);
    } catch (e) {
      console.warn("Falha em bloco de complementos (segue parcial):", e);
    } finally {
      onChunk?.("complementary", i + 1, compChunks.length);
    }
  }
  const complementaryById = indexBy(
    allComplementary,
    (x: any) => x?.assetId ?? x?.id
  );

  // ==== 3) checkups por chassis (paginado)
  const chassisList = Array.from(
    new Set(sourceAssets.map((a) => a.chassis).filter(Boolean))
  );
  const chxChunks = chunk(chassisList, pageSize);
  let allCheckups: any[] = [];
  for (let i = 0; i < chxChunks.length; i++) {
    const c = chxChunks[i];
    try {
      const part = await fetchCheckupsByChassisList(c, sourceAssets);
      if (Array.isArray(part) && part.length)
        allCheckups = allCheckups.concat(part);
    } catch (e) {
      console.warn("Falha em bloco de checkups (segue parcial):", e);
    } finally {
      onChunk?.("checkups", i + 1, chxChunks.length);
    }
  }
  const checkupsByChassis = indexBy(
    allCheckups,
    (x: any) => x?.chassis ?? x?.chassi
  );

  // ==== 4) mapas auxiliares (baseline / conexão)
  const baselineById = indexBy(assets, (a) => a.id);

  const connectionByIdFromProp: Record<string, string | undefined> = {};
  for (const c of assetsConnectionStatus)
    connectionByIdFromProp[c.assetId] = c.connection;

  const resolveConnection = (
    asset: Asset,
    comp: any
  ): "Conectado" | "Não conectado" => {
    const fromProp = normalizeConnection(connectionByIdFromProp[asset.id]);
    if (fromProp) return fromProp;

    const fromComp =
      normalizeConnection(comp?.connection) ||
      normalizeConnection(comp?.connectivity?.status) ||
      normalizeConnection(comp?.device?.status) ||
      normalizeConnection(comp?.status);
    if (fromComp) return fromComp;

    const fromUptime =
      normalizeConnection((asset as any)?.uptime?.connection) ||
      normalizeConnection((asset as any)?.uptime?.connected) ||
      normalizeConnection(comp?.uptime?.connection) ||
      normalizeConnection(comp?.uptime?.connected);
    if (fromUptime) return fromUptime;

    return "Não conectado";
  };

  // ==== 5) RIO plans — usa fetchAssetsByRioPlans (fonte oficial da tela)
  // A função retorna { [nomePlano]: string[] }, então precisamos inverter p/ assetId -> [planos]
  const rioPlansMap = await fetchAssetsByRioPlans(
    summary.map((s) => ({
      id: s.id,
      identification: s.identification,
      name: s.name,
    }))
  );
  onChunk?.("rioplans", 1, 1);

  const rioPlansByAssetId: Record<string, string[]> = {};
  for (const [planName, ids] of Object.entries(rioPlansMap || {})) {
    for (const id of ids || []) {
      if (!rioPlansByAssetId[id]) rioPlansByAssetId[id] = [];
      rioPlansByAssetId[id].push(planName);
    }
  }

  // Fallback (caso queira manter as props/assetsPlans e getAssetRioPlan)
  const propsPlansByAssetId: Record<string, string> = {};
  for (const p of assetsPlans) {
    const list = Array.isArray(p.plan)
      ? p.plan
      : typeof p.plan === "string"
      ? [p.plan]
      : Array.isArray((p as any).plans)
      ? (p as any).plans
      : [];
    propsPlansByAssetId[p.assetId] = normalizePlans(list);
  }

  const resolveRioPlan = (asset: Asset, comp: any): string => {
    const fromFetch = normalizePlans(rioPlansByAssetId[asset.id]);
    if (fromFetch) return fromFetch;

    const fromProps = normalizePlans(propsPlansByAssetId[asset.id]);
    if (fromProps) return fromProps;

    try {
      const svc = getAssetRioPlan?.(asset.id) || {};
      const fromService = normalizePlans((svc as any).plans);
      if (fromService) return fromService;
    } catch {
      /* ignore */
    }

    const fromComp =
      normalizePlans(comp?.plans?.rio) ||
      normalizePlans(comp?.rioPlan) ||
      normalizePlans(comp?.plans?.rioPlan);
    if (fromComp) return fromComp;

    const fromAsset = normalizePlans((asset as any)?.plans);
    if (fromAsset) return fromAsset;

    return "";
  };

  const getLastNextFromCheckups = (asset: Asset) => {
    const c = asset.chassis ? checkupsByChassis[asset.chassis] : undefined;
    const prevList: any[] =
      c?.previous ?? c?.checkups?.previous ?? c?.schedulings?.previous ?? [];
    const nextList: any[] =
      c?.next ?? c?.checkups?.next ?? c?.schedulings?.next ?? [];
    const last =
      Array.isArray(prevList) && prevList.length
        ? prevList[prevList.length - 1]
        : undefined;
    const next =
      Array.isArray(nextList) && nextList.length ? nextList[0] : undefined;

    const lastStr = last
      ? `${last?.checkup?.name ?? last?.name ?? "-"} (${formatDate(
          last?.date
        )})`
      : undefined;
    const nextStr = next
      ? `${next?.checkup?.name ?? next?.name ?? "-"} (${formatDate(
          next?.date
        )})`
      : undefined;
    return { lastStr, nextStr };
  };

  // ==== 6) montar planilha
  const exportData = sourceAssets.map((asset) => {
    const comp = complementaryById[asset.id];
    const base = baselineById[asset.id];

    const accountName =
      asset.accountName ?? comp?.accountName ?? comp?.account?.name ?? "-";

    const groupName =
      asset.groupName ?? comp?.groupName ?? comp?.maintenanceGroup ?? "-";

    const warrantyStatus =
      asset.warrantyStatus ??
      comp?.warrantyStatus ??
      comp?.warranty?.status ??
      "-";

    const volksTotalPlan =
      asset.volksTotalPlan ??
      comp?.volksTotalPlan ??
      comp?.plans?.volksTotal ??
      "";

    const odo =
      asset.odometer ??
      (asset as any)?.uptime?.odometer ??
      comp?.uptime?.odometer ??
      undefined;

    const hr =
      asset.hourmeter ??
      (asset as any)?.uptime?.hourmeter ??
      comp?.uptime?.hourmeter ??
      undefined;

    const { lastStr: lastFromCheckups, nextStr: nextFromCheckups } =
      getLastNextFromCheckups(asset);
    const lastFromAsset = (() => {
      const last = asset.schedulings?.previous?.slice(-1)?.[0];
      return last
        ? `${last.checkup?.name ?? "-"} (${formatDate(last.date)})`
        : undefined;
    })();
    const nextFromAsset = (() => {
      const next = asset.schedulings?.next?.[0];
      return next
        ? `${next.checkup?.name ?? "-"} (${formatDate(next.date)})`
        : undefined;
    })();

    const ultimaRevisao = lastFromCheckups ?? lastFromAsset ?? "-";
    const proximaRevisao = nextFromCheckups ?? nextFromAsset ?? "-";

    const rioPlanText = resolveRioPlan(asset, comp);
    const rioPlan = rioPlanText || "Não";

    const connection = resolveConnection(asset, comp);
    const plate = pickPlate(asset, comp, base);

    return {
      Veículo: asset.name ?? asset.model ?? "-",
      Chassi: asset.chassis ?? "-",
      "Odômetro (km)":
        typeof odo === "number" ? `${odo.toLocaleString("pt-BR")} km` : "-",
      "Horímetro (h)":
        typeof hr === "number" ? `${hr.toLocaleString("pt-BR")} h` : "-",
      Placa: plate,
      Conta: accountName,
      Score: asset.score ?? "-",
      "Grupo de Manutenção": groupName,
      "Status Garantia": warrantyStatus,
      "Status Conexão": connection,
      "Plano Rio": rioPlan,
      "Plano Volks Total": volksTotalPlan || "-",
      "Última Revisão": ultimaRevisao,
      "Próxima Revisão": proximaRevisao,
    };
  });

  if (!exportData.length) throw new Error("Não há registros para exportar.");

  const ws = XLSX.utils.json_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Veículos");
  XLSX.writeFile(wb, filename);
}
